// components/DialogProvider/index.tsx
import { DeleteFilled } from "@ant-design/icons";
import {
  Avatar,
  Button,
  Dialog,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import { PopupTransition } from "components/@extended/Transitions";
import { createContext, ReactNode, useContext, useState } from "react";

type DialogConfirmColor = "error" | "warning" | "success" | "primary" | "secondary" | "info";

interface DialogOptions {
  title: string;
  message?: string;
  confirmText?: string;
  confirmColor?: DialogConfirmColor;
  icon?: ReactNode;
}

interface PromiseInfo {
  resolve: (value: boolean | PromiseLike<boolean>) => void;
  reject: (reason?: any) => void;
}

type ShowDialogHandler = (options: DialogOptions) => Promise<boolean>;

const AVATAR_COLORS: Record<DialogConfirmColor, { bg: string; fg: string }> = {
  error: { bg: "rgb(253,232,231)", fg: "rgb(240,62,52)" },
  warning: { bg: "rgb(255,244,229)", fg: "rgb(183,129,3)" },
  success: { bg: "rgb(224,246,236)", fg: "rgb(21,140,90)" },
  primary: { bg: "rgb(227,238,252)", fg: "rgb(41,98,214)" },
  secondary: { bg: "rgb(238,238,238)", fg: "rgb(97,97,97)" },
  info: { bg: "rgb(224,242,254)", fg: "rgb(2,119,189)" },
};

// Create the context so we can use it in our App
const DialogContext = createContext<ShowDialogHandler>(() => {
  throw new Error("Component is not wrapped with a DialogProvider.");
});

const DialogProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<DialogOptions>({
    title: "",
  });
  const [promiseInfo, setPromiseInfo] = useState<PromiseInfo>();
  const showDialog: ShowDialogHandler = (options) => {
    // When the dialog is shown, keep the promise info so we can resolve later
    return new Promise<boolean>((resolve, reject) => {
      setPromiseInfo({ resolve, reject });
      setOptions(options);
      setOpen(true);
    });
  };
  const handleConfirm = () => {
   
    setOpen(false);
    promiseInfo?.resolve(true);
    setPromiseInfo(undefined);
  };
  const handleCancel = () => {
   
    setOpen(false);
    promiseInfo?.resolve(false);
    setPromiseInfo(undefined);
  };
  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        TransitionComponent={PopupTransition}
      >
        <DialogContent>
          <Stack alignItems="center" spacing={3.5} sx={{ p: 2, pb: 0 }}>
            <Avatar
              color="error"
              sx={{
                width: 72,
                height: 72,
                fontSize: "1.75rem",
                backgroundColor:
                  AVATAR_COLORS[options.confirmColor ?? "error"].bg,
                color: AVATAR_COLORS[options.confirmColor ?? "error"].fg,
              }}
            >
              {options.icon ?? <DeleteFilled />}
            </Avatar>
            <Stack spacing={1.5}>
              <Typography
                variant="h4"
                align="center"
                sx={{ fontWeight: "bold" }}
              >
                {options.title}
              </Typography>
              {options.message && (
                <Typography
                  variant="body1"
                  align="center"
                  color="text.secondary"
                >
                  {options.message}
                </Typography>
              )}
            </Stack>

            <Stack
              direction="row"
              spacing={2}
              sx={{ width: "100%" }}
              justifyContent={"stretch"}
            >
              <Button
                fullWidth
                color={options.confirmColor ?? "error"}
                variant="contained"
                onClick={handleConfirm}
                autoFocus
              >
                {options.confirmText ?? "Delete"}
              </Button>
              <Button
                fullWidth
                onClick={handleCancel}
                color="secondary"
                variant="contained"
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
      <DialogContext.Provider value={showDialog}>
        {children}
      </DialogContext.Provider>
    </>
  );
};


export const useDialog = () => {
  return useContext(DialogContext);
};

export default DialogProvider;
